import type { Territory } from "@/types/supabase";

export type GroupSelectionState = "none" | "partial" | "all";

export interface SelectionChip {
  key: string;
  label: string;
  type: "group" | "item";
  values: string[];
}

export function mergeUniqueSelections(currentValues: string[], incomingValues: string[]): string[] {
  const seen = new Set(currentValues);
  const merged = [...currentValues];

  incomingValues.forEach((value) => {
    if (!seen.has(value)) {
      seen.add(value);
      merged.push(value);
    }
  });

  return merged;
}

export function removeSelections(currentValues: string[], valuesToRemove: string[]): string[] {
  const blocked = new Set(valuesToRemove);
  return currentValues.filter((value) => !blocked.has(value));
}

export function getGroupSelectionState(groupValues: string[], selectedValues: string[]): GroupSelectionState {
  if (groupValues.length === 0) {
    return "none";
  }

  const selectedSet = new Set(selectedValues);
  const selectedCount = groupValues.filter((value) => selectedSet.has(value)).length;

  if (selectedCount === 0) {
    return "none";
  }

  if (selectedCount === groupValues.length) {
    return "all";
  }

  return "partial";
}

export function getGroupedSelectionChips(
  groups: Record<string, string[]>,
  selectedValues: string[]
): SelectionChip[] {
  const selectedSet = new Set(selectedValues);
  const coveredValues = new Set<string>();
  const chips: SelectionChip[] = [];

  Object.entries(groups).forEach(([groupName, groupValues]) => {
    if (groupValues.length > 0 && groupValues.every((value) => selectedSet.has(value))) {
      chips.push({
        key: `group:${groupName}`,
        label: groupName,
        type: "group",
        values: groupValues,
      });

      groupValues.forEach((value) => coveredValues.add(value));
    }
  });

  selectedValues.forEach((value) => {
    if (!coveredValues.has(value)) {
      chips.push({
        key: `item:${value}`,
        label: value,
        type: "item",
        values: [value],
      });
    }
  });

  return chips;
}

export function buildTerritoryGroups(territories: Territory[]): Record<string, string[]> {
  return Object.fromEntries(
    territories.map((territory) => [territory.name, territory.suburbs || []])
  );
}

export function getAreaDisplayLabels(territories: Territory[], selectedValues: string[]): string[] {
  return getGroupedSelectionChips(buildTerritoryGroups(territories), selectedValues).map((chip) => chip.label);
}

export function getAreaSummaryLabel(areaLabels: string[]): string {
  if (areaLabels.length === 0) {
    return "All Hong Kong";
  }

  if (areaLabels.length === 1) {
    return areaLabels[0];
  }

  return `${areaLabels[0]} +${areaLabels.length - 1} more locations`;
}